// Import Packages
import React, { useEffect, useState } from 'react';
import JsonTree from 'autommate-react-json-viewer';
import { useSetRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';

// Import Material UI Elements
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import Button from '@material-ui/core/Button';

// Import state
import { alertState } from '../../../../../recoil-store/recoil-state';

// Import Pages
import { DataTablePreview, DataTableSelection } from './DataTablePreview/DataTablePreview';

// Import CSS
import './fieldValuePreview.css'

// Import Helper
import { GetVariableArrayMatch, numberToLetters, lettersToNumber } from '../../../../../helpers/helper';

function FieldValuePreview({ sourceValue, type, displayName, variables, cancel, saveNewValue, selectedVariableName, dataTableValue, setDataTableValue, selectedVariable, setSelectedVariable }) {
    const [jsonData, setJsonData] = useState({});
    const [value, setValue] = useState();

    const [zoomOut, setZoomOut] = useState(false);
    const setAlert = useSetRecoilState(alertState);

    const { t } = useTranslation();

    const label = 'CONTENT.FLOW.OBJECT-EXPLORER.FIELD-VALUE-PREVIEW.';

    useEffect(() => {
        if (selectedVariableName && sourceValue && type.includes('string-') || (type === 'string' && sourceValue.includes('##'))) {
            setInitialValue()
            setValue(sourceValue);
        } else if (sourceValue.includes('##')) {
            const arrMatch = GetVariableArrayMatch(sourceValue);
            if (arrMatch.length) {
                const variable = variables.find(x => x.name === arrMatch[1]);
                if (!variable) {
                    cancel();
                    setAlert(alert => ({ ...alert, show: true, message: t('FLOW-MESSAGES.LIBRARY.GENERAL.VARIABLE-NOT-FOUND'), severity: 'error' }))
                } else {
                    // if (variable.type !== type) {
                    //     cancel();
                    //     setAlert(alert => ({ ...alert, show: true, message: t('FLOW-MESSAGES.LIBRARY.GENERAL.VARIABLE-NOT-FOUND'), severity: 'error' }))
                    // }
                    if (variable.default_value) {
                        if (arrMatch.length > 2) {
                            try {
                                // check variable type
                                if (variable.type === 'string' || variable.type === 'number' || variable.type === 'boolean' || variable.type === 'date') {
                                    cancel();
                                    setAlert(alert => ({ ...alert, show: true, message: t('FLOW-MESSAGES.LIBRARY.GENERAL.INCORRECT-VARIABLE-USAGE'), severity: 'error' }))
                                } else {
                                    let dv = JSON.parse(variable.default_value);
                                    // get selected element
                                    for (let i = 2; i < arrMatch.length; i++) {
                                        const el = arrMatch[i];
                                        if (dv[el] === undefined) {
                                            cancel();
                                            setAlert(alert => ({ ...alert, show: true, message: t('FLOW-MESSAGES.LIBRARY.GENERAL.ITEM-NOT-FOUND'), severity: 'error' }))
                                            break
                                        } else {
                                            dv = dv[el];
                                        }
                                    }
                                    switch (typeof dv) {
                                        case 'string':
                                        case 'number':
                                        case 'boolean':
                                        case 'date':
                                            break;
                                        default:
                                            dv = JSON.stringify(dv);
                                            break;
                                    }
                                    setValue(dv);
                                }
                            } catch (error) {
                                // err
                                cancel();
                                setAlert(alert => ({ ...alert, show: true, message: t('FLOW-MESSAGES.LIBRARY.GENERAL.FORMAT-ERROR'), severity: 'error' }))
                            }
                        } else {
                            setValue(variable.default_value);
                        }
                    }
                }
            }
        } else {
            setValue(sourceValue);
        }
        if (selectedVariableName) {
            setSelectedVariable(selectedVariableName);
        }
    }, []);

    const changeDataTableZoom = () => {
        setZoomOut(!zoomOut);
    }


    const renderByType = (size) => {
        if ((type.includes('string') && !selectedVariable) && !value || value === '{}' || value === '[]' || (Array.isArray(value) && !value.length)) {
            return <div className='field-value-preview-data'>{t(label + 'NO-DATA-DISPLAY')}</div>
        }
        switch (type) {
            case 'number':
            case 'boolean':
            case 'date':
                return (
                    <div className='field-preview-value border-radius-4'>
                        <div className='preview-string-value'>{value}</div>
                    </div>
                )
            case 'string':
            case 'string-rows':
            case 'string-single-row':
            case 'string-single-column':
            case 'string-columns':
            case 'string-col-name':
            case 'string-range':
                try {
                    let v = value;

                    if (!selectedVariable) {
                        return (
                            <div className='field-preview-value border-radius-4'>
                                <div className='preview-string-value'>{value}</div>
                            </div>
                        )
                    } else {
                        const s = variables.find(x => x.name === selectedVariable)
                        v = s.default_value;
                        if (typeof v === 'string') {
                            v = JSON.parse(v)
                        }
                        return <DataTablePreview type={type} v={v} changeDataTableZoom={changeDataTableZoom} dataTableValue={dataTableValue} setDataTableValue={setDataTableValue} size={size} />
                    }
                } catch (error) {
                    cancel()
                    setAlert(alert => ({ ...alert, show: true, message: t('FLOW-MESSAGES.LIBRARY.GENERAL.FORMAT-ERROR'), severity: 'error' }))
                }
            case 'dictionary':
            case 'object':
            case 'array':
            case 'data table':
                try {
                    let v = value;
                    if (typeof value === 'string') {
                        v = JSON.parse(value)
                    }
                    return <JsonTree data={v} expandable={true} />
                } catch (error) {
                    cancel()
                    setAlert(alert => ({ ...alert, show: true, message: t('FLOW-MESSAGES.LIBRARY.GENERAL.FORMAT-ERROR'), severity: 'error' }))
                }
            default:
                return <div className='field-value-preview-data'>{value}</div>
        }
    }

    const getType = () => {
        if (type.includes('string')) {
            return 'string'
        }
        return type;
    }

    const setInitialValue = () => {
        if (!sourceValue) {
            return;
        }
        try {
            switch (type) {
                case 'string':
                    const arrMatch = GetVariableArrayMatch(sourceValue);
                    if (arrMatch.length === 4) {
                        setDataTableValue({
                            rowIndex: parseInt(arrMatch[2]),
                            colIndex: parseInt(arrMatch[3])
                        });
                    }
                    break;
                case 'string-range':
                    let firstCell;
                    let lastCell;
                    // check multiple select
                    if (sourceValue.includes(':')) {
                        const cells = sourceValue.split(':');
                        firstCell = cells[0];
                        lastCell = cells[1];
                    } else {
                        firstCell = sourceValue;
                    }

                    if (!firstCell.includes('-')) {
                        // format error
                        setAlert(alert => ({ ...alert, show: true, message: t(label + 'RANGE-FORMAT-ERROR'), severity: 'error' }))
                        return;
                    }
                    // ex: C-2 or D-1:E-5
                    const start = firstCell.split('-');
                    let startRow = parseInt(start[1]);
                    let startCol = lettersToNumber(start[0]);
                    if (startCol === 0) {
                        setAlert(alert => ({ ...alert, show: true, message: t(label + 'RANGE-FORMAT-ERROR'), severity: 'error' }))
                        return;
                    }
                    // Subtract 1 to find index values
                    startRow--; startCol--;
                    if (lastCell) {
                        if (!lastCell.includes('-')) {
                            // format error
                            setAlert(alert => ({ ...alert, show: true, message: t(label + 'RANGE-FORMAT-ERROR'), severity: 'error' }))
                            return;
                        }
                        const last = lastCell.split('-');
                        let endRow = parseInt(last[1]);
                        let endCol = lettersToNumber(last[0]);
                        if (endCol === 0) {
                            setAlert(alert => ({ ...alert, show: true, message: t(label + 'RANGE-FORMAT-ERROR'), severity: 'error' }))
                            return;
                        }
                        // Subtract 1 to find index values
                        endRow--; endCol--;
                        let d = [];
                        // Loop through all the rows in the data
                        for (let row = startRow; (startRow <= endRow ? row <= endRow : row >= endRow); startRow <= endRow ? row++ : row--) {
                            // Loop through all the cells in the row
                            for (let col = startCol; startCol <= endCol ? col <= endCol : col >= endCol; startCol <= endCol ? col++ : col--) {
                                // Add a the current cell
                                d.push(`${row.toString()}-${col.toString()}`)
                            }
                        }
                        setDataTableValue(d);
                    } else {
                        setDataTableValue([`${startRow.toString()}-${startCol.toString()}`]);
                    }

                    break;
                case 'string-col-name':
                    const arrayMatch = GetVariableArrayMatch(sourceValue);
                    if (arrayMatch.length === 4) {
                        setDataTableValue(
                            parseInt(arrayMatch[3])
                        );
                    }
                    break;
                case 'string-single-column':
                    const num = lettersToNumber(sourceValue);
                    if (num !== 0) {
                        setDataTableValue(num - 1);
                    }

                    break;
                case 'string-columns':
                    let nums = [];
                    const r = sourceValue.split(',');
                    let err = false;
                    // Loop through all the rows in the data
                    for (let i = 0; i < r.length; i++) {
                        const item = r[i];
                        if (item.includes('-')) {
                            const letters = item.split('-');
                            let firstNum = lettersToNumber(letters[0]);
                            let lastNum = lettersToNumber(letters[1]);
                            if (firstNum !== 0 && lastNum !== 0) {
                                if (firstNum >= lastNum) {
                                    err = true;
                                } else {
                                    // Subtract 1 to find index values
                                    firstNum--; lastNum--;
                                    for (let colIndex = firstNum; colIndex <= lastNum; colIndex++) {
                                        nums.push(colIndex);
                                    }
                                }
                            } else {
                                err = true;
                            }
                        } else {
                            const num = lettersToNumber(item);
                            if (num !== 0) {
                                nums.push(num - 1);
                            } else {
                                err = true;
                            }
                        }

                    }
                    if (!err) {
                        setDataTableValue(nums);
                    }

                    break;
                case 'string-rows':
                case 'string-single-row':
                    let rows = [];
                    // ex. 1-3,5,7,12-17
                    const v = sourceValue.split(',');
                    let error = false;
                    for (let i = 0; i < v.length; i++) {
                        const item = v[i];
                        if (item.includes('-')) {
                            const letters = item.split('-');
                            let firstNum = parseInt(letters[0]);
                            let lastNum = parseInt(letters[1]);
                            if (firstNum !== 0 && lastNum !== 0) {
                                if (firstNum >= lastNum) {
                                    error = true;
                                } else {
                                    // Subtract 1 from the value and have the row index
                                    firstNum--; lastNum--;
                                    for (let colIndex = firstNum; colIndex <= lastNum; colIndex++) {
                                        rows.push(colIndex);
                                    }
                                }
                            } else {
                                error = true;
                            }
                        } else {
                            rows.push(parseInt(item) - 1);
                        }
                    }
                    if (!error) {
                        setDataTableValue(rows);
                    } else {
                        // err
                    }
                default:
                    break;
            }
        } catch (error) {
            // err
        }

    }

    const save = () => {
        if (dataTableValue === undefined || !selectedVariable) {
            return
        }
        try {
            switch (type) {
                case 'string-range':
                    let range = '';
                    const start = dataTableValue[0].split('-');
                    const startRow = parseInt(start[0]);
                    const startCol = parseInt(start[1]);
                    const l = numberToLetters(startCol);
                    if (!l) {
                        // err
                        return
                    }
                    range = `${l}-${startRow + 1}`
                    if (dataTableValue.length > 1) {
                        const end = dataTableValue[dataTableValue.length - 1].split('-');
                        const endRow = parseInt(end[0]);
                        const endCol = parseInt(end[1]);

                        const letter = numberToLetters(endCol);
                        if (!letter) {
                            //err
                            return;
                        }
                        range += `:${letter}-${endRow + 1}`
                    }
                    saveNewValue(range, selectedVariable);
                    break;
                case 'string-col-name':
                    let v = `##${selectedVariable}[0][${dataTableValue}]##`
                    saveNewValue(v, selectedVariable);
                    break;
                case 'string-single-column':
                    const letter = numberToLetters(dataTableValue);
                    if (letter) {
                        saveNewValue(letter, selectedVariable);
                    }
                    break;
                case 'string-columns':
                    dataTableValue.sort(function (a, b) {
                        return a - b;
                    });
                    let letters = '';
                    // input ex:  [0,1,2,3,6,9,10,11]
                    // output ex: 'A-D,G,J-M'
                    for (let i = 0; i < dataTableValue.length; i++) {
                        const num = dataTableValue[i];
                        const letter = numberToLetters(num);
                        if (i === 0) {
                            letters += letter;
                        } else if (dataTableValue[i - 1] + 1 !== num) {
                            letters += `,${letter}`;
                        } else if (dataTableValue.length - 1 >= i + 1) {
                            if (dataTableValue[i + 1] !== num + 1) {
                                letters += `-${letter}`;
                            }
                        } else {
                            letters += `-${letter}`;
                        }
                    }
                    saveNewValue(letters, selectedVariable);
                    break;
                case 'string-rows':
                case 'string-single-row':
                    dataTableValue.sort(function (a, b) {
                        return a - b;
                    });
                    let nums = '';
                    // input ex: [1,2,6,8,9,10]
                    // output ex: '2-3,7,9-11'
                    for (let i = 0; i < dataTableValue.length; i++) {
                        const num = dataTableValue[i];
                        // calculate row number.
                        const row = num + 1;
                        if (i === 0) {
                            nums += row;
                        } else if (dataTableValue[i - 1] + 1 !== num) {
                            nums += `,${row}`;
                        } else if (dataTableValue.length - 1 >= i + 1) {
                            // looking at the difference with the next value
                            if (dataTableValue[i + 1] !== num + 1) {
                                nums += `-${row}`;
                            }
                        } else {
                            nums += `-${row}`;
                        }
                    }
                    saveNewValue(nums, selectedVariable);
                    break;
                default:
                    break;
            }
        } catch (error) {
            // err
        }
    }

    return (
        <>
            <div className='field-value-preview-content'>
                <div className='field-preview-label'>{t(label + 'FIELD-NAME')}</div>
                <div className='field-preview-value'>{displayName}</div>

                <div className='field-preview-label'>{t(label + 'FIELD-TYPE')}</div>
                <div className='field-preview-value'>{getType()}</div>

                <div className='field-preview-label'>{t(label + 'SELECT-SOURCE-VARIABLE')}</div>
                <div className='field-preview-value'>
                    {<DataTableSelection selectedVariable={selectedVariable} setSelectedVariable={setSelectedVariable} variables={variables}
                        setDataTableValue={setDataTableValue} />}
                </div>

                <div className='field-preview-label'>
                    {t(label + 'FIELD-VALUE-PREVIEW')}
                    {
                        type.includes('string') && selectedVariable &&
                        <div className='data-table-selection-description'>{t(label + "DATA-TABLE-SELECTION-DESCRIPTION." + type.toUpperCase())}</div>
                    }
                </div>

                {renderByType('medium')}
                {
                    zoomOut &&
                    < Dialog open={zoomOut} maxWidth={'lg'}>

                        <DialogContent className='' >
                            <div >
                                {renderByType('large')}
                            </div>

                        </DialogContent>
                    </Dialog >

                }

            </div>
            {type.includes('string-') &&
                <div className='rigth-page-footer'>
                    <Button disabled={dataTableValue === undefined ? true : false}
                        className={`${dataTableValue === undefined ? 'pasif-save-button' : 'active-save-button'} object-explorer-save-button`}
                        onClick={save} variant="contained" size="medium" >
                        {t('CONTENT.FLOW.OBJECT-EXPLORER.SAVE')}
                    </Button>
                </div>
            }
        </>

    )
}

export default FieldValuePreview;